import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { projects } from '../data/mock';
import DeviceMockup from '../components/DeviceMockup';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import MouseGlow from '../components/MouseGlow';
import { localizedPath } from '../utils/routes';
import { usePageTransition } from '../components/PageTransition';
import { SEO } from '../components/SEO';

const CATEGORIES = ['all', 'Website', 'Web App', 'Brand Design'];

const PortfolioPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'it';
  const [activeFilter, setActiveFilter] = useState('all');
  const { transitionTo } = usePageTransition();

  const filterKey = (cat) => {
    if (cat === 'all') return 'filter_all';
    if (cat === 'Website') return 'filter_website';
    if (cat === 'Web App') return 'filter_webapp';
    if (cat === 'Brand Design') return 'filter_brand';
    return 'filter_all';
  };

  const filtered = activeFilter === 'all'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  const handleProjectClick = (project) => {
    if (project.isRealProject && project.slug) {
      transitionTo(localizedPath(currentLang, 'project', { slug: project.slug }));
    }
  };

  return (
    <div className="bg-[#0a0a0a] relative film-grain overflow-x-hidden min-h-screen">
      <SEO page="portfolio" />
      <MouseGlow />
      <CustomCursor />
      <Navbar />

      <main className="relative pt-32 pb-20" style={{ zIndex: 1 }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
              {t('portfolio_page.subtitle')}
            </span>
            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[0.9] mb-6">
              {t('portfolio_page.title')}{' '}
              <span className="text-outline">{t('portfolio_page.title_outline')}</span>
            </h1>
            <p className="text-white/70 text-lg font-light max-w-xl">
              {t('portfolio_page.description')}
            </p>
          </motion.div>

          {/* Category Filters */}
          <motion.div
            className="flex flex-wrap gap-3 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
                onClick={() => setActiveFilter(cat)}
                className={`px-6 py-3 text-xs tracking-[0.2em] uppercase transition-all duration-400 border ${
                  activeFilter === cat
                    ? 'bg-[#FFD700] text-black border-[#FFD700] font-bold'
                    : 'bg-transparent text-white/60 border-white/15 hover:border-[#FFD700]/50 hover:text-white'
                }`}
              >
                {t(`portfolio_page.${filterKey(cat)}`)}
              </button>
            ))}
          </motion.div>

          {/* Projects Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              className="grid md:grid-cols-2 gap-8 lg:gap-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {filtered.length === 0 ? (
                <p className="text-white/40 text-lg col-span-2 text-center py-20">
                  {t('portfolio_page.no_projects')}
                </p>
              ) : (
                filtered.map((project, index) => {
                  const tp = (field) => {
                    const key = `case_study.projects.${project.id}.${field}`;
                    const translated = t(key, { returnObjects: true, defaultValue: null });
                    if (translated && translated !== key) return translated;
                    return project[field];
                  };

                  return (
                    <motion.div
                      key={project.id}
                      className={`group ${project.isRealProject ? 'cursor-pointer' : ''}`}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      onClick={() => handleProjectClick(project)}
                      data-testid={`portfolio-project-${project.slug || project.id}`}
                    >
                      <div className="relative bg-gradient-to-br from-white/[0.06] to-white/[0.01] border border-white/10 rounded-2xl overflow-hidden group-hover:border-[#FFD700]/30 transition-all duration-500">
                        {/* Badge bar */}
                        <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/5">
                          <div className="flex items-center gap-3">
                            {project.clientLogoMark ? (
                              <img
                                src={project.clientLogoMark}
                                alt={`${project.title} logo`}
                                className="w-9 h-9 object-contain"
                              />
                            ) : (
                              <span className="font-display text-xl text-[#FFD700]/40">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                            )}
                            {project.location && (
                              <div className="flex items-center gap-1.5 text-white/40">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                <span className="text-[10px] tracking-wider uppercase">{tp('location')}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {project.isRealProject && (
                              <span className="bg-[#FFD700] text-black text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 font-bold rounded-full">
                                {t('case_study.real_project')}
                              </span>
                            )}
                            <span className="text-white/20 text-xs">{project.category}</span>
                          </div>
                        </div>

                        {/* Image */}
                        <div className="relative p-4 md:p-6 bg-gradient-to-b from-transparent to-black/20">
                          {project.desktopImage && project.mobileImage ? (
                            <DeviceMockup
                              desktopImage={project.desktopImage}
                              mobileImage={project.mobileImage}
                              projectTitle={project.title}
                            />
                          ) : (
                            <div className="relative aspect-video overflow-hidden rounded-lg">
                              <img
                                src={project.image}
                                alt={project.title}
                                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                              />
                              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-all duration-500" />
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="px-4 md:px-5 pb-4 md:pb-5 flex gap-3">
                          {project.isRealProject && project.slug && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleProjectClick(project); }}
                              className="flex-1 py-2.5 text-[10px] tracking-[0.2em] uppercase text-white/60 border border-white/10 hover:border-[#FFD700]/40 hover:text-[#FFD700] transition-all duration-300 text-center"
                              data-testid={`view-case-study-${project.slug}`}
                            >
                              {t('portfolio_page.view_case_study')}
                            </button>
                          )}
                          {project.link && (
                            <a
                              href={project.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 py-2.5 text-[10px] tracking-[0.2em] uppercase text-[#FFD700] border border-[#FFD700]/40 bg-[#FFD700]/5 hover:bg-[#FFD700]/15 transition-all duration-300 text-center flex items-center justify-center gap-2"
                              data-testid={`visit-site-${project.slug || project.id}`}
                            >
                              {t('portfolio_page.visit_site')}
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Info below card */}
                      <div className="mt-5 space-y-2 px-1">
                        <div className="flex items-center justify-between">
                          <span className="text-[#FFD700] text-xs tracking-[0.3em] uppercase">
                            {project.category}
                          </span>
                          {project.year && (
                            <span className="text-white/30 text-sm">{project.year}</span>
                          )}
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl text-white group-hover:text-[#FFD700] transition-colors duration-500">
                          {tp('title')}
                        </h3>
                        <p className="text-white/40 font-light text-sm leading-relaxed">
                          {tp('description')}
                        </p>
                        {project.services && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            {(tp('services') || []).slice(0, 4).map((service) => (
                              <span
                                key={service}
                                className="text-[10px] tracking-[0.12em] uppercase text-white/25 border border-white/8 px-2 py-0.5"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </motion.div>
          </AnimatePresence>

          {/* Bottom CTA */}
          <motion.div
            className="mt-24 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="w-16 h-px bg-[#FFD700]/30 mx-auto mb-8" />
            <p className="text-white/60 text-lg font-light mb-8">
              {t('portfolio_page.contact_cta')}
            </p>
            <motion.button
              onClick={() => {
                navigate(localizedPath(currentLang, 'home'));
                setTimeout(() => {
                  const el = document.getElementById('contatti') || document.getElementById('contact') || document.getElementById('kontakt');
                  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
                }, 500);
              }}
              className="px-10 py-4 bg-[#FFD700] text-black font-display text-sm tracking-widest hover:bg-[#FFD700]/90 transition-colors duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="portfolio-contact-cta"
            >
              {t('portfolio_page.contact_cta_button')}
            </motion.button>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
